import { useMemo, useState } from "react";
import { Calculator, Info } from "lucide-react";
import { useLang } from "@/lib/lang-context";

/**
 * Italian acquisition-cost calculator for residential / agricultural real estate.
 *
 * Why this exists
 * ---------------
 * Salento buyers (especially non-residents) routinely under-estimate total
 * purchase cost by 10-15%. They see "€75.000" on the listing and forget
 * about notaio, imposta di registro, agenzia. This component renders the
 * full breakdown next to the asking price so the Evaluation Sheet shows
 * what the buyer actually wires at the closing.
 *
 * Tax rules applied (privati, no IVA — typical Salento second-home flow)
 * ---------------------------------------------------------------------
 * * Imposta di registro:
 *     - Prima casa: 2% of cadastral value (here we approximate on price for
 *       simplicity — actual catastale is usually lower, so this is a
 *       conservative upper-bound), minimum €1.000.
 *     - Seconda casa / non residents: 9% of price, minimum €1.000.
 *     - Agricultural land (NOT IAP / coltivatore diretto): 15% of price,
 *       minimum €1.000 (purchase by private buyers attracts higher tax).
 * * Imposta ipotecaria: €50 fixed (private flat).
 * * Imposta catastale: €50 fixed (private flat).
 * * Notaio: ~2% of price, typical Salento private-transaction range.
 *   Editable via slider so the user can match their actual quote.
 * * Agenzia: 3% + IVA(22%) = 3.66% (typical Italian agency commission).
 *   Toggle off when buying privately.
 *
 * All percentages are configurable so the user can paste their own quote and
 * see the real total. This is the differentiator: nobody else surfaces this.
 */

const fmtEUR = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(n);

export const AcquisitionCost = ({ price, isAgricultural }) => {
  const { t } = useLang();
  // Default to the most common Salento case: non-resident buying a second
  // home from an agency. Users can flip everything inline.
  const [firstHome, setFirstHome] = useState(false);
  const [agencyOn, setAgencyOn] = useState(true);
  const [notaryPct, setNotaryPct] = useState(2.0);

  const breakdown = useMemo(() => {
    if (!price || price <= 0) return null;

    // Imposta di registro — agri overrides residential treatment.
    let registryRate;
    if (isAgricultural) {
      registryRate = 0.15; // privati without coltivatore diretto status
    } else {
      registryRate = firstHome ? 0.02 : 0.09;
    }
    const registry = Math.max(1000, price * registryRate);

    // Fixed flats (imposte ipotecaria + catastale, private flow).
    const fixedDuties = 50 + 50;

    // Notary — slider-controlled.
    const notary = price * (notaryPct / 100);

    // Agency — 3% + IVA 22% = 3.66% — toggle.
    const agency = agencyOn ? price * 0.0366 : 0;

    const totalExtra = registry + fixedDuties + notary + agency;
    const totalAll = price + totalExtra;
    const extraPct = (totalExtra / price) * 100;

    return {
      registry,
      registryRate,
      fixedDuties,
      notary,
      agency,
      totalExtra,
      totalAll,
      extraPct,
    };
  }, [price, firstHome, agencyOn, notaryPct, isAgricultural]);

  if (!breakdown) return null;

  return (
    <div
      data-testid="acquisition-cost"
      className="px-6 md:px-10 py-8 border-t border-[#E6E4DD] bg-[#FAF9F4]"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Calculator size={12} strokeWidth={1.5} />
            {t("cost.title")}
          </div>
          <p className="mt-2 text-sm text-[#6C706D] max-w-2xl">{t("cost.sub")}</p>
        </div>

        {/* Inline toggle controls — visible at-a-glance so the user knows what assumptions drive the totals. */}
        <div className="flex flex-wrap gap-3 items-center text-xs">
          {!isAgricultural && (
            <button
              data-testid="cost-toggle-firsthome"
              onClick={() => setFirstHome((v) => !v)}
              className={`px-3 py-1.5 font-semibold uppercase tracking-wider border transition-colors ${
                firstHome
                  ? "bg-[#4A5D23] text-white border-[#3B4A1C]"
                  : "bg-white text-[#282C2A] border-[#E6E4DD] hover:border-[#282C2A]"
              }`}
            >
              {firstHome ? t("cost.firstHomeOn") : t("cost.firstHomeOff")}
            </button>
          )}
          <button
            data-testid="cost-toggle-agency"
            onClick={() => setAgencyOn((v) => !v)}
            className={`px-3 py-1.5 font-semibold uppercase tracking-wider border transition-colors ${
              agencyOn
                ? "bg-[#4A5D23] text-white border-[#3B4A1C]"
                : "bg-white text-[#282C2A] border-[#E6E4DD] hover:border-[#282C2A]"
            }`}
          >
            {agencyOn ? t("cost.agencyOn") : t("cost.agencyOff")}
          </button>
          <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-[#E6E4DD]">
            <span className="font-mono text-[10px] text-[#6C706D] uppercase tracking-wider">
              {t("cost.notary")} <span className="text-[#282C2A]">{notaryPct.toFixed(1)}%</span>
            </span>
            <input
              data-testid="cost-notary-slider"
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={notaryPct}
              onChange={(e) => setNotaryPct(Number(e.target.value))}
              className="w-24 accent-[#4A5D23]"
              aria-label={t("cost.notary")}
            />
          </label>
        </div>
      </div>

      <dl className="grid grid-cols-1 md:grid-cols-5 gap-x-6 gap-y-3 text-sm">
        <CostRow label={t("cost.askingPrice")} value={fmtEUR(price)} testId="cost-base" />
        <CostRow
          label={`${t("cost.registry")} (${(breakdown.registryRate * 100).toFixed(0)}%)`}
          value={`+ ${fmtEUR(breakdown.registry)}`}
          testId="cost-registry"
        />
        <CostRow
          label={t("cost.fixedDuties")}
          value={`+ ${fmtEUR(breakdown.fixedDuties)}`}
          testId="cost-fixed"
          hint={t("cost.fixedDutiesHint")}
        />
        <CostRow
          label={`${t("cost.notary")} (${notaryPct.toFixed(1)}%)`}
          value={`+ ${fmtEUR(breakdown.notary)}`}
          testId="cost-notary"
        />
        <CostRow
          label={agencyOn ? t("cost.agency") : t("cost.agencyOff")}
          value={agencyOn ? `+ ${fmtEUR(breakdown.agency)}` : "—"}
          testId="cost-agency"
        />
      </dl>

      <div className="mt-6 pt-5 border-t-2 border-[#282C2A] flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">{t("cost.totalLabel")}</div>
          <div
            className="font-serif-display text-4xl md:text-5xl text-[#282C2A] leading-none mt-2"
            data-testid="cost-total"
          >
            {fmtEUR(breakdown.totalAll)}
          </div>
        </div>
        <div className="text-right">
          <div className="label-eyebrow">{t("cost.extraLabel")}</div>
          <div className="font-mono text-2xl text-[#A0522D] mt-2" data-testid="cost-extra">
            + {fmtEUR(breakdown.totalExtra)}{" "}
            <span className="text-sm text-[#6C706D]">
              ({breakdown.extraPct.toFixed(1)}%)
            </span>
          </div>
        </div>
      </div>

      <p className="mt-5 text-[11px] text-[#6C706D] flex items-start gap-1.5 max-w-3xl">
        <Info size={11} strokeWidth={1.5} className="mt-0.5 shrink-0" />
        <span>{t("cost.disclaimer")}</span>
      </p>
    </div>
  );
};

const CostRow = ({ label, value, testId, hint }) => (
  <div className="flex flex-col" data-testid={testId}>
    <dt className="label-eyebrow truncate" title={hint || label}>
      {label}
    </dt>
    <dd className="font-mono text-base mt-1 text-[#282C2A]">{value}</dd>
  </div>
);

export default AcquisitionCost;
